// ============================================================
// ConsentUrlDialog.js
// One-time-display modal shared by ALL the consent-URL generator
// families (Federation, M365 Identity Lifecycle, Emergency Pack /
// Document Auth). Built on the shared <Modal> — native <dialog>
// handles focus trap + ESC. The title/intro props MUST be rendered,
// not just declared: this dialog names the app the client's GA is
// about to consent — the defaults describe Federation, and every
// other family passes its own wording. (The 2026-08-29 fix: the
// props existed but the body hardcoded the Federation strings, so
// the Lifecycle dialog had mis-named its app since it shipped.)
//
// The URL is operator-paste-or-click-through material: shared with
// the client tenant's M365 administrator who completes the Microsoft
// consent flow. Closing the dialog discards the URL — a fresh URL
// is one POST away if the operator needs to retrieve it again.
// ============================================================

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './ConsentUrlDialog.css';

export default function ConsentUrlDialog({
  consentUrl,
  onClose,
  title = 'Federation consent URL',
  intro = "Share this URL with the client tenant's M365 administrator. They will be redirected to Microsoft to grant admin consent for the A2Z CareHub Federation app registration.",
}) {
  const [copied, setCopied] = useState(false);

  // Reset the "Copied!" label after 2s so subsequent copy clicks
  // get fresh visual feedback. Cleanup on unmount avoids the
  // setState-on-unmounted warning.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
    } catch (err) {
      // Clipboard API can fail in non-secure contexts (HTTP) or when
      // the page hasn't been focused. Surface the URL anyway — the
      // operator can still select-and-copy manually.
      // eslint-disable-next-line no-console
      console.error('ConsentUrlDialog: clipboard write failed', err);
    }
  }

  function handleOpenInNewTab() {
    window.open(consentUrl, '_blank', 'noopener,noreferrer');
  }

  // isOpen is implicitly true: the dialog only mounts when there's
  // a URL to display. The parent unmounts on close.
  return (
    <Modal isOpen={true} onClose={onClose} title={title}>
      <div className="consent-url-dialog">
        <p className="consent-url-dialog__intro">
          {intro}
        </p>

        <pre className="consent-url-dialog__url" data-testid="consent-url-display">
          {consentUrl}
        </pre>

        <div className="consent-url-dialog__actions">
          <button
            type="button"
            className="button-primary"
            onClick={handleCopy}
          >
            {copied ? 'Copied!' : 'Copy to clipboard'}
          </button>
          <button
            type="button"
            className="button-secondary"
            onClick={handleOpenInNewTab}
          >
            Open in new tab
          </button>
          <button
            type="button"
            className="button-secondary"
            onClick={onClose}
            data-testid="consent-url-dialog-close"
          >
            Close
          </button>
        </div>

        <p className="consent-url-dialog__footnote">
          This URL is one-time-displayed. Closing this dialog discards it. Generate a new
          URL if you need to retrieve it again.
        </p>
      </div>
    </Modal>
  );
}
